import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  Building2,
  CalendarDays,
  CreditCard,
  ExternalLink,
  LayoutDashboard,
  Loader2,
  LogOut,
  Menu,
  Percent,
  Settings,
  X,
  Users,
} from 'lucide-react';
import { auth } from '@/store/authStore';
import { clubsBookingApi, type AdminSubscriptionAccessStatus } from '@/api/clubs-booking';
import { useAdminSubscriptionRouteSnapshot } from '@/contexts/AdminSubscriptionRouteContext';
import { useAuth } from '@/contexts/AuthContext';
import { useStripeConnectStatus } from '@/contexts/StripeConnectStatusContext';
import { getClubSiteHref } from '@/utils/publicSite';

type AdminShellProps = {
  title: string;
  subtitle?: string;
  mobileTopRight?: ReactNode;
  children: ReactNode;
};

const navItems = [
  { icon: LayoutDashboard, label: 'Dashboard', to: '/dashboard' },
  { icon: CalendarDays, label: 'Activities', to: '/activities' },
  { icon: Users, label: 'Attendees', to: '/attendees' },
  { icon: Users, label: 'Users', to: '/users', ownerOnly: true },
  { icon: CreditCard, label: 'Bookings', to: '/bookings' },
  { icon: Percent, label: 'Discounts', to: '/discounts' },
  { icon: Building2, label: 'Club Profile', to: '/club-profile' },
];

const AdminShell = ({ title, subtitle, mobileTopRight, children }: AdminShellProps) => {
  const routeSubscriptionSnapshot = useAdminSubscriptionRouteSnapshot();
  const skipInitialSubscriptionFetchRef = useRef(!!routeSubscriptionSnapshot?.status);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [subscriptionAccess, setSubscriptionAccess] = useState<AdminSubscriptionAccessStatus | null>(
    () => routeSubscriptionSnapshot?.status ?? null,
  );
  const [subscriptionAccessReceivedAtMs, setSubscriptionAccessReceivedAtMs] = useState<number | null>(
    () => routeSubscriptionSnapshot?.receivedAtMs ?? null,
  );
  const [liveSiteHref, setLiveSiteHref] = useState('');
  const { status: stripeConnectStatus, loading: stripeConnectStatusLoading, isStripeReady } = useStripeConnectStatus();
  const [countdownNowMs, setCountdownNowMs] = useState(() => Date.now());
  const user = auth.getUser();
  const { handleLogout } = useAuth();

  useEffect(() => {
    if (!routeSubscriptionSnapshot) return;
    setSubscriptionAccess(routeSubscriptionSnapshot.status);
    setSubscriptionAccessReceivedAtMs(routeSubscriptionSnapshot.receivedAtMs);
  }, [routeSubscriptionSnapshot?.receivedAtMs, routeSubscriptionSnapshot?.status]);

  useEffect(() => {
    if (!user?.isAuthenticated || user.role === 'parent') {
      setSubscriptionAccess(null);
      setSubscriptionAccessReceivedAtMs(null);
      return;
    }

    let active = true;

    const loadSubscriptionAccess = async () => {
      try {
        const status = await clubsBookingApi.getAdminSubscriptionAccessStatus(true, true);
        if (!active) return;
        setSubscriptionAccess(status);
        setSubscriptionAccessReceivedAtMs(Date.now());
      } catch {
        if (!active) return;
        setSubscriptionAccess(null);
        setSubscriptionAccessReceivedAtMs(null);
      }
    };

    if (skipInitialSubscriptionFetchRef.current) {
      skipInitialSubscriptionFetchRef.current = false;
    } else {
      void loadSubscriptionAccess();
    }
    const refreshInterval = window.setInterval(() => {
      void loadSubscriptionAccess();
    }, 60 * 1000);

    return () => {
      active = false;
      window.clearInterval(refreshInterval);
    };
  }, [user?.isAuthenticated, user?.role]);

  useEffect(() => {
    if (!user?.isAuthenticated || user.role === 'parent') {
      setLiveSiteHref('');
      return;
    }

    let active = true;

    const loadLiveSiteHref = async () => {
      try {
        const clubProfile = await clubsBookingApi.getMyClubProfile();
        if (!active) return;
        setLiveSiteHref(clubProfile?.publicSlug ? getClubSiteHref(clubProfile.publicSlug) : '');
      } catch {
        if (!active) return;
        setLiveSiteHref('');
      }
    };

    void loadLiveSiteHref();

    return () => {
      active = false;
    };
  }, [user?.isAuthenticated, user?.role]);

  const openSubscriptionPortal = async () => {
    try {
      const { url } = await clubsBookingApi.createAdminSubscriptionPortalLink({
        returnUrl: `${window.location.origin}/billing`,
      });
      window.location.assign(url);
    } catch {
      // Ignore in shell-level UI; billing page handles portal errors with richer feedback.
    }
  };

  useEffect(() => {
    if (!subscriptionAccess?.cancelAtPeriodEnd && !subscriptionAccess?.trialActive) return;
    const tickInterval = window.setInterval(() => {
      setCountdownNowMs(Date.now());
    }, 1000);
    return () => {
      window.clearInterval(tickInterval);
    };
  }, [subscriptionAccess?.cancelAtPeriodEnd, subscriptionAccess?.trialActive]);

  const cancellationCountdown = useMemo(() => {
    if (!subscriptionAccess?.cancelAtPeriodEnd) return null;

    let endsAtMs = 0;
    const explicitEndsAt = subscriptionAccess.subscriptionEndsAt
      ? new Date(subscriptionAccess.subscriptionEndsAt)
      : null;

    if (explicitEndsAt && !Number.isNaN(explicitEndsAt.getTime())) {
      endsAtMs = explicitEndsAt.getTime();
    } else if (
      subscriptionAccess.subscriptionSecondsRemaining > 0 &&
      subscriptionAccessReceivedAtMs
    ) {
      endsAtMs = subscriptionAccessReceivedAtMs + (subscriptionAccess.subscriptionSecondsRemaining * 1000);
    }

    if (!endsAtMs) return null;

    const secondsRemaining = Math.max(0, Math.floor((endsAtMs - countdownNowMs) / 1000));
    const days = Math.floor(secondsRemaining / (24 * 60 * 60));
    const hours = Math.floor((secondsRemaining % (24 * 60 * 60)) / (60 * 60));
    const minutes = Math.floor((secondsRemaining % (60 * 60)) / 60);
    const seconds = secondsRemaining % 60;
    const countdownLabel = days > 0
      ? `${days}d ${hours}h ${minutes}m`
      : hours > 0
        ? `${hours}h ${minutes}m`
        : minutes > 0
          ? `${minutes}m ${seconds}s`
          : `${seconds}s`;

    const endsAt = new Date(endsAtMs);

    return {
      secondsRemaining,
      endsAtLabel: endsAt.toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }),
      countdownLabel,
    };
  }, [
    subscriptionAccess?.cancelAtPeriodEnd,
    subscriptionAccess?.subscriptionEndsAt,
    subscriptionAccess?.subscriptionSecondsRemaining,
    subscriptionAccessReceivedAtMs,
    countdownNowMs,
  ]);

  const trialCountdown = useMemo(() => {
    if (!subscriptionAccess?.trialActive) return null;

    let endsAtMs = 0;
    const explicitEndsAt = subscriptionAccess.trialEndsAt
      ? new Date(subscriptionAccess.trialEndsAt)
      : null;

    if (explicitEndsAt && !Number.isNaN(explicitEndsAt.getTime())) {
      endsAtMs = explicitEndsAt.getTime();
    } else if (
      subscriptionAccess.trialSecondsRemaining > 0 &&
      subscriptionAccessReceivedAtMs
    ) {
      endsAtMs = subscriptionAccessReceivedAtMs + (subscriptionAccess.trialSecondsRemaining * 1000);
    }

    if (!endsAtMs) return null;

    const secondsRemaining = Math.max(0, Math.floor((endsAtMs - countdownNowMs) / 1000));
    const days = Math.floor(secondsRemaining / (24 * 60 * 60));
    const hours = Math.floor((secondsRemaining % (24 * 60 * 60)) / (60 * 60));
    const minutes = Math.floor((secondsRemaining % (60 * 60)) / 60);
    const seconds = secondsRemaining % 60;
    const countdownLabel = days > 0
      ? `${days}d ${hours}h ${minutes}m`
      : hours > 0
        ? `${hours}h ${minutes}m`
        : minutes > 0
          ? `${minutes}m ${seconds}s`
          : `${seconds}s`;

    const endsAt = new Date(endsAtMs);

    return {
      secondsRemaining,
      endsAtLabel: endsAt.toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }),
      countdownLabel,
    };
  }, [
    subscriptionAccess?.trialActive,
    subscriptionAccess?.trialEndsAt,
    subscriptionAccess?.trialSecondsRemaining,
    subscriptionAccessReceivedAtMs,
    countdownNowMs,
  ]);

  const showStripeSetupWarning = useMemo(() => {
    const showTrialBanner = !!subscriptionAccess?.trialActive && !subscriptionAccess?.hasActiveSubscription;
    if (showTrialBanner) return false;
    if (stripeConnectStatusLoading) return false;
    if (!stripeConnectStatus) return true;
    return !isStripeReady;
  }, [
    subscriptionAccess?.trialActive,
    subscriptionAccess?.hasActiveSubscription,
    stripeConnectStatusLoading,
    stripeConnectStatus,
    isStripeReady,
  ]);

  const renderNav = (isMobile = false) => (
    <>
      <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
        {navItems
          .filter((item) => !item.ownerOnly || user?.isOrganizationOwner)
          .map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={() => isMobile && setDrawerOpen(false)}
            className={({ isActive }) =>
              `flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left text-sm transition ${
                isActive ? 'bg-black/20 font-medium' : 'text-white/80 hover:bg-white/10'
              }`
            }
          >
            <item.icon className="h-4 w-4" />
            {item.label}
          </NavLink>
        ))}
      </nav>
      <div className="mt-auto border-t border-white/10 px-3 pb-6 pt-4">
        {subscriptionAccess?.trialActive && !subscriptionAccess?.hasActiveSubscription && (
          <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            <p className="font-semibold text-amber-800">Free trial active.</p>
            <p className="mt-1 font-medium">
              {trialCountdown
                ? `${trialCountdown.countdownLabel} remaining in your trial.`
                : `${Math.max(0, Number(subscriptionAccess.trialDaysRemaining || 0))} day(s) remaining in your trial.`}
            </p>
            {user?.isOrganizationOwner ? (
              <NavLink
                to="/billing-required"
                onClick={() => isMobile && setDrawerOpen(false)}
                className="mt-2 inline-flex rounded-md border border-amber-400 bg-white px-2.5 py-1.5 font-semibold text-amber-900 hover:bg-amber-100"
              >
                Start subscription
              </NavLink>
            ) : (
              <p className="mt-2 text-amber-800">Please contact the organization owner to start the subscription.</p>
            )}
          </div>
        )}
        {subscriptionAccess?.cancelAtPeriodEnd && (
          <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900">
            <p className="font-semibold text-red-700">Your account has been cancelled.</p>
            <p className="mt-1 font-medium">
              {cancellationCountdown ? `${cancellationCountdown.countdownLabel} remaining until your account is turned off.` : ''}
            </p>
            <p className="mt-1 text-red-700">
              {user?.isOrganizationOwner ? (
                <>If this is a mistake, you can renew your subscription <button onClick={() => void openSubscriptionPortal()} style={{ textDecoration: 'underline' }}>here</button>.</>
              ) : (
                'Please contact the organization owner if this cancellation needs to be reversed.'
              )}
            </p>
          </div>
        )}
        {/*stripeConnectStatusLoading && (
          <div
            className="mb-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
            data-testid="sidebar-stripe-warning-loading"
          >
            <div className="flex items-center gap-2">
              <Loader2 className="h-3.5 w-3.5 animate-spin text-amber-700" />
              <p className="font-semibold text-amber-800">Checking for notifications...</p>
            </div>
          </div>
        )*/}
        {showStripeSetupWarning && (
          <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="sidebar-stripe-warning">
            <p className="font-semibold text-amber-800">Online payments are currently off, so parents cannot pay online.</p>
            {user?.isOrganizationOwner ? (
              <NavLink
                to="/bookings"
                onClick={() => isMobile && setDrawerOpen(false)}
                className="mt-2 inline-flex rounded-md border border-amber-400 bg-white px-2.5 py-1.5 font-semibold text-amber-900 hover:bg-amber-100"
              >
                Set up online payments
              </NavLink>
            ) : (
              <p className="mt-2 text-amber-800">Please contact the organization owner to finish Stripe setup.</p>
            )}
          </div>
        )}
        {user?.isOrganizationOwner ? (
          <NavLink
            to="/billing"
            onClick={() => isMobile && setDrawerOpen(false)}
            className={({ isActive }) =>
              `mb-1 flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left text-sm transition ${
                isActive ? 'bg-black/20 font-medium' : 'text-white/80 hover:bg-white/10'
              }`
            }
          >
            <Settings className="h-4 w-4" />
            Billing
          </NavLink>
        ) : null}
        {liveSiteHref ? (
          <a
            href={liveSiteHref}
            target="_blank"
            rel="noreferrer"
            onClick={() => isMobile && setDrawerOpen(false)}
            className="mb-1 flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left text-sm text-white/80 transition hover:bg-white/10"
          >
            <ExternalLink className="h-4 w-4" />
            Visit Live Site
          </a>
        ) : null}
        <button
          className="flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left text-sm text-white/80 hover:bg-white/10"
          onClick={handleLogout}
        >
          <LogOut className="h-4 w-4" />
          Logout
        </button>
      </div>
    </>
  );

  return (
    <main className="min-h-[100dvh] overflow-x-clip bg-[#f3f4f8] text-[#242a3a]">
      <div className="mx-auto w-full lg:pl-64">
        {drawerOpen && (
          <div className="fixed inset-0 z-40 bg-black/40 lg:hidden" onClick={() => setDrawerOpen(false)} />
        )}
        <aside className={`fixed inset-y-0 left-0 z-50 flex w-72 flex-col bg-[#17754D] text-white transition-transform duration-200 lg:hidden ${drawerOpen ? 'translate-x-0' : '-translate-x-full pointer-events-none'}`}>
          <div className="flex items-center justify-between border-b border-white/10 px-6 py-5">
            <div className="flex items-center gap-2">
              <img src="/logo.png" alt="Session Monkey" className="h-8 w-8 object-contain" />
              <div className="whitespace-nowrap text-lg font-semibold tracking-tight">Session Monkey</div>
            </div>
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              className="rounded-md p-1 text-white/90 hover:bg-white/10"
              aria-label="Close menu"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          {renderNav(true)}
        </aside>

        <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 bg-[#17754D] text-white lg:flex lg:flex-col">
          <div className="border-b border-white/10 px-6 py-6">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="Session Monkey" className="h-8 w-8 object-contain" />
              <div className="whitespace-nowrap text-lg font-semibold tracking-tight">Session Monkey</div>
            </div>
          </div>
          {renderNav()}
        </aside>

        <div className="min-w-0 flex-1 px-3 py-4 sm:px-4 sm:py-5 md:px-8 md:py-6 lg:px-10" data-testid="admin-shell-content">
          <div className="mb-2 flex items-center justify-between gap-3 lg:hidden">
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm"
              aria-label="Open menu"
            >
              <Menu className="h-4 w-4" />
              Menu
            </button>
            {mobileTopRight ? <div className="text-sm">{mobileTopRight}</div> : <div />}
          </div>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3 md:mb-5 md:gap-4">
          <div>
              <h1 className="text-xl font-semibold leading-tight sm:text-2xl md:text-4xl">{title}</h1>
              {subtitle && <p className="mt-0.5 max-w-2xl text-sm text-slate-500 md:mt-1 md:text-base">{subtitle}</p>}
            </div>
          </div>

          {children}
        </div>
      </div>
    </main>
  );
};

export default AdminShell;
